import React from 'react';
import { observer } from 'mobx-react';

import { Badge, Button, Input, Label, Select, Table, Tbody, Td, Th, Thead, Tr } from 'common/materials';

import FguiPortManagementViewModel, { toMappingKey } from './FguiPortManagement.viewmodel';

const PORT_TYPE_OPTIONS = [
    { label: 'พอร์ตมาตรฐาน', value: 'standard' as const },
    { label: 'พอร์ตอื่น ๆ', value: 'other' as const }
];

const STATUS_OPTIONS = [
    { label: 'active', value: 'active' as const },
    { label: 'inactive', value: 'inactive' as const }
];

function FguiPortManagement() {
    const {
        userRole,
        companyMaster,
        profiles,
        profileForm,
        isEditingProfile,
        setProfileForm,
        handleEditProfile,
        handleResetProfile,
        handleSaveProfile,
        selectedCompany,
        companyPorts,
        guiApplications,
        companyPortForm,
        setCompanyPortForm,
        handleSelectCompany,
        handleEditCompanyPort,
        handleSelectApplication,
        handleSaveCompanyPort,
        handleDeleteCompanyPort
    } = FguiPortManagementViewModel();

    if (userRole !== 'Super Admin') {
        return (
            <React.Fragment>
                <p className="text-2xl font-bold">FGUI Port Management</p>
                <p className="p-4 text-neutral-500">Only Super Admin can access this page.</p>
            </React.Fragment>
        );
    }

    const profileOptions = profiles.map(profile => ({
        label: `${profile.profile_name}${profile.status === 'inactive' ? ' (inactive)' : ''}`,
        value: profile.profile_id
    }));
    const applicationOptions = guiApplications.map(app => ({
        label: `${app.application_name.replace('\n', ' ')} (${app.application_id} / ${app.platform_id})`,
        value: toMappingKey(app),
        app
    }));

    return (
        <div className="flex flex-col gap-6">
            <p className="text-2xl font-bold">FGUI Port Management</p>

            {/* 1. Port names (shared by every company) */}
            <section className="flex flex-col gap-3">
                <p className="text-lg font-bold">1. ชื่อพอร์ต (ทุกบริษัทเห็นเหมือนกัน)</p>

                <div className="grid grid-cols-1 gap-3 rounded-lg bg-neutral-50 p-3 md:grid-cols-6">
                    <div className="flex flex-col gap-1">
                        <Label>Port ID</Label>
                        <Input
                            placeholder="FI-AR"
                            value={profileForm.profile_id}
                            disabled={isEditingProfile}
                            onChange={e => setProfileForm({ ...profileForm, profile_id: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1 md:col-span-2">
                        <Label>ชื่อที่แสดง</Label>
                        <Input
                            placeholder="FI-AR 8080"
                            value={profileForm.profile_name}
                            onChange={e => setProfileForm({ ...profileForm, profile_name: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Label>ประเภท</Label>
                        <Select
                            options={PORT_TYPE_OPTIONS}
                            value={PORT_TYPE_OPTIONS.find(o => o.value === profileForm.port_type)}
                            onChange={o => o && setProfileForm({ ...profileForm, port_type: o.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Label>ลำดับ</Label>
                        <Input
                            type="number"
                            value={profileForm.sort_order}
                            onChange={e => setProfileForm({ ...profileForm, sort_order: Number(e.target.value) || 0 })}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Label>สถานะ</Label>
                        <Select
                            options={STATUS_OPTIONS}
                            value={STATUS_OPTIONS.find(o => o.value === profileForm.status)}
                            onChange={o => o && setProfileForm({ ...profileForm, status: o.value })}
                        />
                    </div>
                    <div className="flex items-end gap-2 md:col-span-6 md:justify-end">
                        <Button
                            outline
                            onClick={handleResetProfile}
                        >
                            {isEditingProfile ? 'ยกเลิก' : 'ล้าง'}
                        </Button>
                        <Button
                            className="bg-primary-900 text-white"
                            onClick={handleSaveProfile}
                        >
                            {isEditingProfile ? 'บันทึกการแก้ไข' : 'เพิ่มพอร์ต'}
                        </Button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Port ID</Th>
                                <Th>ชื่อที่แสดง</Th>
                                <Th>ประเภท</Th>
                                <Th>ลำดับ</Th>
                                <Th>สถานะ</Th>
                                <Th />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {profiles.length === 0 ? (
                                <Tr>
                                    <Td colSpan={6}>ยังไม่มีพอร์ต</Td>
                                </Tr>
                            ) : (
                                profiles.map(profile => (
                                    <Tr key={profile.profile_id}>
                                        <Td>{profile.profile_id}</Td>
                                        <Td>{profile.profile_name}</Td>
                                        <Td>{profile.port_type === 'standard' ? 'พอร์ตมาตรฐาน' : 'พอร์ตอื่น ๆ'}</Td>
                                        <Td>{profile.sort_order}</Td>
                                        <Td>
                                            <Badge>{profile.status}</Badge>
                                        </Td>
                                        <Td>
                                            <Button
                                                outline
                                                size="sm"
                                                onClick={() => handleEditProfile(profile)}
                                            >
                                                แก้ไข
                                            </Button>
                                        </Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </div>
            </section>

            {/* 2. IP / Port of each company */}
            <section className="flex flex-col gap-3">
                <p className="text-lg font-bold">2. IP / Port ของแต่ละบริษัท</p>

                <div className="flex flex-col gap-1 md:w-1/2">
                    <Label>Company</Label>
                    <Select
                        isClearable
                        placeholder="กรุณาเลือก Company"
                        options={companyMaster}
                        getOptionLabel={company => company.company_name}
                        getOptionValue={company => company.sid}
                        value={selectedCompany ?? null}
                        onChange={company => handleSelectCompany(company ?? null)}
                    />
                </div>

                {selectedCompany && (
                    <React.Fragment>
                        <div className="grid grid-cols-1 gap-3 rounded-lg bg-neutral-50 p-3 md:grid-cols-6">
                            <div className="flex flex-col gap-1">
                                <Label>พอร์ต</Label>
                                <Select
                                    placeholder="เลือกพอร์ต"
                                    options={profileOptions}
                                    value={profileOptions.find(o => o.value === companyPortForm.profile_id) ?? null}
                                    onChange={o => o && setCompanyPortForm({ ...companyPortForm, profile_id: o.value })}
                                />
                            </div>
                            <div className="flex flex-col gap-1 md:col-span-2">
                                <Label>แอป GUI</Label>
                                <Select
                                    placeholder={guiApplications.length === 0 ? 'บริษัทนี้ไม่มีแอป GUI' : 'เลือกแอป GUI'}
                                    isDisabled={guiApplications.length === 0}
                                    options={applicationOptions}
                                    value={applicationOptions.find(o => o.value === companyPortForm.mapping_key) ?? null}
                                    onChange={o => handleSelectApplication(o?.app ?? null)}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label>IP</Label>
                                <Input
                                    placeholder="10.0.1.20"
                                    value={companyPortForm.host}
                                    onChange={e => setCompanyPortForm({ ...companyPortForm, host: e.target.value })}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label>Port</Label>
                                <Input
                                    placeholder="8080"
                                    value={companyPortForm.port}
                                    onChange={e => setCompanyPortForm({ ...companyPortForm, port: e.target.value })}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label>สถานะ</Label>
                                <Select
                                    options={STATUS_OPTIONS}
                                    value={STATUS_OPTIONS.find(o => o.value === companyPortForm.status)}
                                    onChange={o => o && setCompanyPortForm({ ...companyPortForm, status: o.value })}
                                />
                            </div>
                            <div className="flex items-end justify-end md:col-span-6">
                                <Button
                                    className="bg-primary-900 text-white"
                                    onClick={handleSaveCompanyPort}
                                >
                                    บันทึก
                                </Button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <Table>
                                <Thead>
                                    <Tr>
                                        <Th>พอร์ต</Th>
                                        <Th>แอป GUI</Th>
                                        <Th>IP</Th>
                                        <Th>Port</Th>
                                        <Th>สถานะ</Th>
                                        <Th>แก้ไขล่าสุด</Th>
                                        <Th />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {companyPorts.length === 0 ? (
                                        <Tr>
                                            <Td colSpan={7}>บริษัทนี้ยังไม่ได้ตั้งค่าพอร์ต</Td>
                                        </Tr>
                                    ) : (
                                        companyPorts.map(companyPort => (
                                            <Tr key={companyPort.profile_id}>
                                                <Td>{companyPort.profile_name}</Td>
                                                <Td>
                                                    {companyPort.application_name?.replace('\n', ' ') ?? (
                                                        <span className="text-red-600">ไม่พบแอป (ถูกลบไปแล้ว?)</span>
                                                    )}
                                                </Td>
                                                <Td>{companyPort.host}</Td>
                                                <Td>{companyPort.port}</Td>
                                                <Td>
                                                    <Badge>{companyPort.status}</Badge>
                                                </Td>
                                                <Td className="text-xs text-neutral-500">
                                                    {companyPort.updated_on} {companyPort.updated_by}
                                                </Td>
                                                <Td>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            outline
                                                            size="sm"
                                                            onClick={() => handleEditCompanyPort(companyPort)}
                                                        >
                                                            แก้ไข
                                                        </Button>
                                                        <Button
                                                            outline
                                                            size="sm"
                                                            className="text-red-600"
                                                            onClick={() => handleDeleteCompanyPort(companyPort)}
                                                        >
                                                            ลบ
                                                        </Button>
                                                    </div>
                                                </Td>
                                            </Tr>
                                        ))
                                    )}
                                </Tbody>
                            </Table>
                        </div>
                    </React.Fragment>
                )}
            </section>
        </div>
    );
}

export default observer(FguiPortManagement);
